import type { StaticImageData } from 'next/image';
// Backgrounds are sized for the published camera (720p) and served as WebP:
// they are downloaded on the first pick, often over a slow VPN, so a 1.2 MB
// photo meant a ten-second wait. The picker shows separate tiny thumbnails.
import Desk from '@/public/background-images/desk.webp';
import DeskThumb from '@/public/background-images/desk-thumb.webp';
import Nature from '@/public/background-images/nature.webp';
import NatureThumb from '@/public/background-images/nature-thumb.webp';
import { ru } from './ru';

export type BackgroundOption =
  | { id: string; kind: 'none'; label: string }
  | { id: string; kind: 'blur'; label: string; blurRadius: number }
  | { id: string; kind: 'image'; label: string; src: string; thumb: string };

export const NONE_BACKGROUND_ID = 'none';

/** Uploaded backgrounds get ids like `custom-<uuid>`, never colliding with the static ones. */
export const CUSTOM_BACKGROUND_PREFIX = 'custom-';

export function isCustomBackgroundId(id: string): boolean {
  return id.startsWith(CUSTOM_BACKGROUND_PREFIX);
}

const image = (img: StaticImageData) => img.src;

export const BACKGROUND_OPTIONS: BackgroundOption[] = [
  { id: NONE_BACKGROUND_ID, kind: 'none', label: ru.backgroundNone },
  { id: 'blur', kind: 'blur', label: ru.backgroundBlur, blurRadius: 10 },
  { id: 'blur-strong', kind: 'blur', label: ru.backgroundBlurStrong, blurRadius: 25 },
  { id: 'desk', kind: 'image', label: ru.backgroundDesk, src: image(Desk), thumb: image(DeskThumb) },
  { id: 'nature', kind: 'image', label: ru.backgroundNature, src: image(Nature), thumb: image(NatureThumb) },
];

// Custom (uploaded) backgrounds merge after the static ones. The merged list is
// cached so React's useSyncExternalStore gets a stable snapshot between changes.
let customOptions: BackgroundOption[] = [];
let optionsSnapshot: BackgroundOption[] = BACKGROUND_OPTIONS;
const optionsListeners = new Set<() => void>();

/** All options: the static ones first, then the user's uploaded backgrounds. */
export function getBackgroundOptions(): BackgroundOption[] {
  return optionsSnapshot;
}

/** Replaces the custom part of the list; every picker re-renders with the new tiles. */
export function setCustomOptions(options: BackgroundOption[]) {
  customOptions = options;
  optionsSnapshot = [...BACKGROUND_OPTIONS, ...customOptions];
  optionsListeners.forEach((listener) => listener());
}

export function subscribeToBackgroundOptions(listener: () => void) {
  optionsListeners.add(listener);
  return () => {
    optionsListeners.delete(listener);
  };
}

export function findBackgroundOption(id: string): BackgroundOption {
  return (
    BACKGROUND_OPTIONS.find((option) => option.id === id) ??
    customOptions.find((option) => option.id === id) ??
    BACKGROUND_OPTIONS[0]
  );
}
